import React from 'react'
import PropTypes from 'prop-types'
import { Alert, AlertTitle } from '@material-ui/lab'
import { find } from 'lodash'

import helpers from 'helpers'

import constants from 'constants/index'
import useStyles from './styles'

// `action` (ex.: "Ver parecer") fica dentro da faixa de status, para o parecer não abrir uma segunda faixa.
const ActivityAlert = ({ activity, modifiedForm, action }) => {
  const { ARCHIVED_STATUS_ID } = constants.governanceProject
  const {
    AUDIT_AGREES_STATUS_ID,
    COMPLETED_STATUS_ID,
    IN_PROGRESS_STATUS_ID,
    OVERDUE_STATUS_ID,
    REJECTED_STATUS_ID,
    RISK_ASSUMED_STATUS_ID,
  } = constants.governanceProjectActivity

  const classes = useStyles()

  const isOverdueDate = activity?.deadlineDate > new Date()

  const openReview = activity?.openReview
  const latestReview = activity?.latestReview
  const isCoveredByReview = !!activity?.coveredByReview
  // No envio pela organização a Revisão nasce na mesma transação; só a entrada de cobertura com controles já em
  // awaiting_approval a abre de forma assíncrona. Nesse intervalo não há `openReview`: aguarda revisão sem autor nem data.
  const isInReview = helpers.governanceProject.isActivityInReview(activity)
  const isAwaitingApproval =
    helpers.governanceProject.isActivityAwaitingApproval(activity)
  const isCompleted = activity?.status === COMPLETED_STATUS_ID
  const isInProgress = activity?.status === IN_PROGRESS_STATUS_ID
  const isOverdue = activity?.status === OVERDUE_STATUS_ID
  const isRejected = activity?.status === REJECTED_STATUS_ID
  const isRiskAssumed = activity?.status === RISK_ASSUMED_STATUS_ID
  const isRejectedInReview = isCoveredByReview && isRejected
  // Reprovado no fluxo antigo, antes da cobertura, não tem parecer: cai na faixa genérica de reprovado.
  const isReviewRejected = latestReview?.status === REJECTED_STATUS_ID

  const submissionDescription = !openReview
    ? ''
    : openReview.submittedBy?.name
    ? `Enviado para revisão por ${
        openReview.submittedBy.name
      } em ${helpers.governanceProject.formatReviewDateTime(
        openReview.submittedAt,
      )}. `
    : `Enviado em ${helpers.governanceProject.formatReviewDateTime(
        openReview.submittedAt,
        'DD/MM/YYYY',
      )}. `

  const governanceProject = activity?.governanceProject
  // true também quando o projeto está arquivado, não só concluído
  const isCompletedProject = helpers.governanceProject.isProjectLocked(
    governanceProject?.status,
  )
  const isArchivedProject = governanceProject?.status === ARCHIVED_STATUS_ID
  const underContestation = governanceProject?.underContestation
  const underContestationAudit = governanceProject?.underContestationAudit

  const auditStage = activity?.governanceProjectCompanyStage
  const auditEntities = auditStage?.governanceProjectAuditEntities
  const auditorAnswer = find(auditEntities, { kind: 'answer_audit' })

  const auditorAnswerEquals =
    activity?.contestationStatus === AUDIT_AGREES_STATUS_ID

  if (isArchivedProject) {
    return (
      <Alert action={action} severity="warning" variant="standard">
        <AlertTitle>Este projeto foi arquivado.</AlertTitle>
        Esta atividade não pode ser editada porque o projeto está arquivado.
      </Alert>
    )
  }

  if (isCompletedProject && (underContestation || underContestationAudit)) {
    if (activity?.contestation) {
      if (underContestationAudit) {
        return (
          <Alert action={action} severity="info" variant="standard">
            A contestação desta atividade está em andamento. Aguarde a análise
            do auditor.
          </Alert>
        )
      }

      return (
        <Alert action={action} severity="info" variant="standard">
          Esta atividade está sob contestação.
        </Alert>
      )
    }

    if (!!auditorAnswer?.entityId && !underContestationAudit) {
      if (auditorAnswerEquals) {
        return (
          <Alert action={action} severity="info" variant="standard">
            O auditor concordou com a resposta da organização ou a melhorou.
          </Alert>
        )
      }

      return (
        <Alert action={action} severity="warning" variant="standard">
          O auditor não concordou com a resposta da organização. Por favor,
          verifique as observações feitas pelo auditor e, se necessário, realize
          uma contestação.
        </Alert>
      )
    }

    return action ? (
      <Alert action={action} severity="info" variant="standard">
        O revisor enviou um parecer sobre este controle.
      </Alert>
    ) : null
  }

  if (isRiskAssumed) {
    return (
      <Alert
        action={action}
        severity="info"
        variant="standard"
        icon={false}
        className={classes.riskAssumedAlert}
      >
        <AlertTitle>Risco assumido pela organização.</AlertTitle>
        {!!latestReview &&
          `Registrado por ${
            latestReview.closedBy?.name || '-'
          } em ${helpers.governanceProject.formatReviewDateTime(
            latestReview.closedAt,
          )}. Justificativa: ${latestReview.riskJustification || '-'}`}
      </Alert>
    )
  }

  if (isRejectedInReview && isReviewRejected) {
    return (
      <Alert action={action} severity="warning" variant="standard">
        <AlertTitle>Controle reprovado.</AlertTitle>
        Faça os ajustes indicados no parecer e envie para revisão novamente, ou
        assuma o risco.
      </Alert>
    )
  }

  if (isCoveredByReview && isCompleted && !modifiedForm) {
    return (
      <Alert action={action} severity="success" variant="standard">
        <AlertTitle>Controle aprovado.</AlertTitle>
        {!!latestReview?.latestSentOpinion?.outcomeAt &&
          `Aprovado em ${helpers.governanceProject.formatReviewDateTime(
            latestReview.latestSentOpinion.outcomeAt,
          )}.`}
      </Alert>
    )
  }

  if (isRejected) {
    return (
      <Alert action={action} severity="error" variant="standard">
        <AlertTitle>Esta atividade foi reprovada!</AlertTitle>
        Revise as pendências apontadas, realize os ajustes necessários e, quando
        estiver pronto, reenvie para nova avaliação. Se precisar de ajuda,
        consulte o responsável pelo projeto.
      </Alert>
    )
  }

  if (isInReview) {
    return (
      <Alert action={action} severity="info" variant="standard">
        <AlertTitle>Controle aguardando revisão.</AlertTitle>
        {submissionDescription}
        Enquanto aguarda a revisão, só é possível alterar o responsável e o
        prazo.
      </Alert>
    )
  }

  if (isAwaitingApproval && !modifiedForm) {
    return (
      <Alert action={action} severity="warning" variant="standard">
        <AlertTitle>Esta atividade está aguardando aprovação.</AlertTitle>
        Se você for o <strong>responsável</strong> por todos os projetos ou esse
        projeto em específico, por favor, analise a atividade e realize a
        aprovação ou reprovação conforme necessário. Caso contrário, aguarde a
        conclusão da análise para que a atividade possa ser finalizada.
      </Alert>
    )
  }

  if (isInProgress || (modifiedForm && !isOverdueDate)) {
    return (
      <Alert action={action} severity="info" variant="standard">
        <AlertTitle>Esta atividade encontra-se pendente.</AlertTitle>
        Por favor, conclua todas as etapas necessárias antes do prazo e
        finalize-a utilizando o botão disponível no final da página.
      </Alert>
    )
  }

  if (isCompleted && !modifiedForm) {
    return (
      <Alert action={action} severity="success" variant="standard">
        <AlertTitle>Esta atividade foi concluída.</AlertTitle>
        Para realizar alterações, certifique-se de fazê-las antes do
        encerramento do projeto ou do prazo limite.
      </Alert>
    )
  }

  if (isOverdue || (modifiedForm && isOverdueDate)) {
    return (
      <Alert action={action} severity="error" variant="standard">
        <AlertTitle>Esta atividade está atrasada!</AlertTitle>
        Por favor, conclua todas as etapas necessárias o mais rápido possível e
        encaminhe para revisão utilizando o botão disponível no final da página.
      </Alert>
    )
  }

  return action ? (
    <Alert action={action} severity="info" variant="standard">
      O revisor enviou um parecer sobre este controle.
    </Alert>
  ) : null
}

ActivityAlert.propTypes = {
  activity: PropTypes.object,
  modifiedForm: PropTypes.bool,
  action: PropTypes.node,
}

export default ActivityAlert
